import {
  Box,
  Button,
  Link,
  Paper,
  Stack,
  Typography,
  styled,
} from '@mui/material';
import type { TypographyProps } from '@mui/material/Typography';
import { pxToRem } from '~/theme/typography';

const CARD = {
  maxWidth: 1192,
  radius: 16,
  shadow: '0px 2px 12px rgba(145, 158, 171, 0.20)',
} as const;

const MEDIA = {
  width: 376,
  height: 236,
  radius: 8,
} as const;

/** 左欄為 YouTube 靜態預覽圖時，中央灰色播放鈕（純 CSS） */
const YOUTUBE_THUMB_OVERLAY = {
  playDiameter: 56,
  triangleHeight: 11,
  triangleWidth: 18,
  /** 三角形視覺置中微調 */
  playIconNudgePx: 5,
} as const;

const RIGHT = {
  width: 752,
  titleRadius: 8,
  titlePadY: 12,
  titlePadX: 16,
} as const;

export const latestNewsAccordionCard = {
  Root: styled(Paper)(({ theme }) => ({
    width: '100%',
    maxWidth: CARD.maxWidth,
    borderRadius: `${CARD.radius}px`,
    backgroundColor: theme.palette.common.white,
    boxShadow: theme.palette.mode === 'light' ? CARD.shadow : theme.shadows[1],
    overflow: 'hidden',
  })),

  Inner: styled(Box)(({ theme }) => ({
    padding: theme.spacing(2.5), // 20px @ 8px spacing unit
  })),

  Row: styled(Stack)(({ theme }) => ({
    width: '100%',
    alignItems: 'stretch',
    gap: theme.spacing(3), // 24px
  })),

  LeftColumn: styled(Box)(() => ({
    width: MEDIA.width,
  })),

  Cover: styled(Box)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    alignSelf: 'start',
    '&[role="button"]': {
      cursor: 'pointer',
      '&:focus-visible': {
        outline: '2px solid',
        outlineColor: theme.palette.primary.main,
        outlineOffset: 2,
      },
    },
  })),

  /** 縮圖 + 疊層的定位容器 */
  CoverMedia: styled(Box)(() => ({
    position: 'relative',
    width: '100%',
    aspectRatio: '4 / 3',
    maxHeight: '240px',
    borderRadius: '8px',
    overflow: 'hidden',
    flexShrink: 0,
    cursor: 'pointer',
  })),

  CoverImage: styled('img')(() => ({
    width: '100%',
    height: '100%',
    objectFit: 'contain',
    objectPosition: 'center center',
    display: 'block',
    position: 'absolute',
    inset: '0px',
  })),

  /** YouTube 預覽圖上提示可播放；點擊由外層 Cover 處理 */
  YoutubePlayOverlay: styled(Box)(() => ({
    position: 'absolute',
    inset: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    pointerEvents: 'none',
  })),

  YoutubePlayCircle: styled(Box)(() => ({
    width: YOUTUBE_THUMB_OVERLAY.playDiameter,
    height: YOUTUBE_THUMB_OVERLAY.playDiameter,
    borderRadius: '50%',
    backgroundColor: 'rgba(97, 97, 97, 0.82)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.25)',
    paddingLeft: `${YOUTUBE_THUMB_OVERLAY.playIconNudgePx}px`,
  })),

  YoutubePlayTriangle: styled(Box)(() => ({
    width: 0,
    height: 0,
    borderStyle: 'solid',
    borderWidth: `${YOUTUBE_THUMB_OVERLAY.triangleHeight}px 0 ${YOUTUBE_THUMB_OVERLAY.triangleHeight}px ${YOUTUBE_THUMB_OVERLAY.triangleWidth}px`,
    borderColor:
      'transparent transparent transparent rgba(255, 255, 255, 0.95)',
  })),

  TagsWrap: styled(Box)(() => ({
    marginTop: '12px',
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: '16px',
    width: '100%',
    maxWidth: '100%',
    minWidth: 0,
  })),

  Tags: styled(Typography)<TypographyProps>(({ theme }) => ({
    fontSize: pxToRem(14),
    lineHeight: `${18}px`,
    fontWeight: 400,
    color: theme.palette.grey[600],
  })),

  Date: styled(Typography)<TypographyProps>(({ theme }) => ({
    marginTop: '12px',
    fontSize: pxToRem(14),
    lineHeight: `${18}px`,
    fontWeight: 400,
    color: theme.palette.grey[500],
  })),

  RightColumn: styled(Box)(() => ({
    flex: '1 1 auto',
    minWidth: 0,
    width: RIGHT.width,
    maxWidth: '100%',
    display: 'flex',
    flexDirection: 'column',
  })),

  TitleBar: styled(Box)(({ theme }) => ({
    backgroundColor: theme.palette.secondary.lighter,
    borderRadius: `${RIGHT.titleRadius}px`,
    padding: `${RIGHT.titlePadY}px ${RIGHT.titlePadX}px`,
  })),

  Title: styled(Typography)<TypographyProps>(({ theme }) => ({
    fontFamily: 'Noto Sans TC',
    fontWeight: 700,
    fontSize: pxToRem(20),
    lineHeight: `${30}px`,
    color: theme.palette.text.secondary,
    wordBreak: 'break-word',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
  })),

  BodyShell: styled(Box)(({ theme }) => ({
    flex: 1,
    minHeight: 0,
    marginTop: theme.spacing(1.5), // 12px
    display: 'flex',
    flexDirection: 'column',
  })),

  /** 內文區：依內容高度自然撐開（不要把附件吸到很遠的下方） */
  BodyGrow: styled(Box)(() => ({
    flex: '0 1 auto',
    minHeight: 0,
  })),

  /** 吃掉標題下方剩餘高度，讓附件/按鈕維持在卡片下緣區塊 */
  BodySpacer: styled(Box)(() => ({
    flex: 1,
    minHeight: 0,
  })),

  /** 內文 + 附件：作為一個整塊往上靠，避免中間被 flex 空白撐開 */
  BodyUpper: styled(Box)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    flex: '0 1 auto',
    minHeight: 0,
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
  })),

  /**
   * 收合預覽：與展開內文同一套 HTML，保留段落、換行、粗體與連結等排版；
   * 仍以 line-clamp 限制高度；內嵌媒體先隱藏，避免佔滿預覽行數。
   */
  CollapsedBody: styled(Box)(({ theme }) => ({
    fontFamily: 'Noto Sans TC',
    fontWeight: 500,
    fontSize: pxToRem(18),
    lineHeight: `${24}px`,
    color: theme.palette.text.primary,
    wordBreak: 'break-word',
    display: '-webkit-box',
    WebkitLineClamp: 6,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    '& p': { marginBottom: theme.spacing(1.875), marginTop: 0 },
    '& p:last-child': { marginBottom: 0 },
    '& a': { color: theme.palette.info.dark },
    '& img, & iframe, & video, & object, & embed': {
      display: 'none',
    },
  })),

  ExpandedBody: styled(Box)(({ theme }) => ({
    fontSize: pxToRem(18),
    lineHeight: `${24}px`,
    fontWeight: 500,
    color: theme.palette.text.primary,
    wordBreak: 'break-word',
    '& p': { marginBottom: theme.spacing(1.875), marginTop: 0 },
    '& p:last-child': { marginBottom: 0 },
    '& a': { color: theme.palette.info.dark },
    '& img': {
      maxWidth: '100%',
      height: 'auto',
      borderRadius: `${MEDIA.radius}px`,
      marginTop: theme.spacing(1.875),
      marginBottom: theme.spacing(1.875),
      display: 'block',
      marginLeft: 'auto',
      marginRight: 'auto',
    },
    '& iframe, & video, & embed': {
      display: 'block',
      width: '100%',
      maxWidth: '100%',
      aspectRatio: '16 / 9',
      height: 'auto',
      marginTop: theme.spacing(1.875),
      marginBottom: theme.spacing(1.875),
      marginLeft: 'auto',
      marginRight: 'auto',
      border: 0,
    },
  })),

  /**
   * 附件橫向緊接排列（flex-wrap），滿行才換行；不拉滿等分欄故不會出現大空白。
   * 約兩列高度為上限，再多則在此區塊內垂直捲動。
   */
  AttachmentList: styled(Box)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'center',
    alignContent: 'flex-start',
    gap: theme.spacing(0.5),
    marginTop: theme.spacing(2),
    width: '100%',
    maxWidth: '100%',
    minWidth: 0,
    maxHeight: `calc(48px + ${theme.spacing(3)})`,
    overflowY: 'auto',
    overflowX: 'hidden',
    WebkitOverflowScrolling: 'touch',
  })),

  AttachmentRow: styled(Box)(({ theme }) => ({
    display: 'inline-flex',
    flexDirection: 'row',
    alignItems: 'center',
    flex: '0 0 auto',
    gap: theme.spacing(0.25),
    minWidth: 0,
    maxWidth: '100%',
    '&:hover .latest-news-attachmentIcon .MuiSvgIcon-root': {
      color: theme.palette.info.dark,
    },
    '&:hover .latest-news-attachmentLink': {
      color: theme.palette.info.dark,
      textDecoration: 'underline',
      textDecorationColor: theme.palette.info.dark,
    },
  })),

  AttachmentIcon: styled(Box)(({ theme }) => ({
    display: 'inline-flex',
    alignItems: 'center',
    flexShrink: 0,
    lineHeight: 0,
    margin: 0,
    padding: 0,
    '& .MuiSvgIcon-root': {
      color: theme.palette.info.main,
      margin: 0,
      padding: 0,
      display: 'block',
      transition: theme.transitions.create(['color'], {
        duration: theme.transitions.duration.shorter,
      }),
    },
  })),

  AttachmentLink: styled(Link)(({ theme }) => ({
    fontFamily: 'Noto Sans TC',
    fontWeight: 600,
    fontSize: pxToRem(15),
    lineHeight: `${22}px`,
    display: 'block',
    flex: '0 1 auto',
    minWidth: 0,
    width: 'max-content',
    maxWidth: '100%',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    color: theme.palette.info.main,
    textDecoration: 'none',
    padding: 0,
    margin: 0,
    transition: theme.transitions.create(['color', 'text-decoration-color'], {
      duration: theme.transitions.duration.shorter,
    }),
  })),

  ButtonWrap: styled(Box)(({ theme }) => ({
    flexShrink: 0,
    paddingTop: theme.spacing(3), // 24px
  })),

  ToggleButton: styled(Button)(({ theme }) => ({
    height: '42px',
    borderRadius: `${MEDIA.radius}px`,
    backgroundColor: theme.palette.secondary.light,
    color: theme.palette.text.secondary,
    fontFamily: 'Noto Sans TC',
    fontWeight: 700,
    fontSize: pxToRem(18),
    lineHeight: `${24}px`,
    boxShadow: 'none',
    textTransform: 'none',
    '&:hover': {
      backgroundColor: theme.palette.secondary.main,
      boxShadow: 'none',
    },
  })),
};
